import { BadgeVariant } from "shared/components/Badge";
import type {
  EmployerVerification,
  EmployerVerificationStatus
} from "../../../types/income";

/**
 * BSCPF-6584. What the Employer verification tab says about itself.
 *
 * The design carries the status on the tab strip rather than inside the panel, and a
 * completed verification that contradicts the applicant shows the mismatch count
 * *instead of* "Completed" — the contradiction is the more useful thing to see without
 * opening the tab.
 *
 * The count comes from the API (`mismatch_count`), so this badge and the warning banner
 * inside the panel cannot disagree about how many there are.
 */

const EMPLOYER_STATUS_LABEL: Record<EmployerVerificationStatus, string> = {
  pending: "Waiting for employer",
  started: "Waiting for employer",
  completed: "Completed",
  declined: "Declined",
  no_response: "No response",
  canceled: "Canceled",
  failed: "Failed"
};

// Matches LandLordSurveyStatusBadge, the same badge on the sibling survey: anything
// still waiting on the recipient is WARNING, and a survey that ran out of road —
// no response, failed — is ERROR. Only an explicit cancel is neutral.
const EMPLOYER_STATUS_VARIANT: Record<
  EmployerVerificationStatus,
  BadgeVariant
> = {
  pending: BadgeVariant.WARNING,
  started: BadgeVariant.WARNING,
  completed: BadgeVariant.SUCCESS,
  declined: BadgeVariant.ERROR,
  no_response: BadgeVariant.ERROR,
  canceled: BadgeVariant.BASE,
  failed: BadgeVariant.ERROR
};

/** The states where the ball is in the supervisor's court. */
const WAITING_STATUSES: EmployerVerificationStatus[] = ["pending", "started"];

export const employerTabBadge = (
  employerVerification: EmployerVerification
) => {
  const { status, mismatch_count: mismatches } = employerVerification;

  // Only a completed verification can have contradicted anything; a decline or a
  // no-response has no answers, and a count there would be masking the real state.
  if (status === "completed" && mismatches > 0)
    return {
      variant: BadgeVariant.WARNING,
      label: mismatches === 1 ? "1 mismatch" : `${mismatches} mismatches`,
      waiting: false
    };

  return {
    variant: EMPLOYER_STATUS_VARIANT[status],
    label: EMPLOYER_STATUS_LABEL[status],
    // Still on the supervisor. The caller pairs this with the spinner the landlord
    // survey's badge uses, rather than returning JSX from a plain data helper.
    waiting: WAITING_STATUSES.includes(status)
  };
};
